import * as hooks from "lib/hooks";
import React, {
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState,
} from "react";
import * as result from "lib/result";
import * as util from "lib/util";
import { Containment, ContainmentDTO } from "models";
import { Box, Button, Divider, Paper, Typography, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import * as routes from "enums/routes";
import { Dropdown, FormCard } from "components";
import { LoadingButton } from "pages";
import {
	OBJECT_ID_ASRS_LOCK,
	OBJECT_ID_ASRS_UNLOCK,
	OBJECT_ID_CREATE_CONTAINMENT,
	OBJECT_ID_HOLD_CONTAINMENT,
} from "pages/Users/constants";
import { UploadContentTypeLPN } from "lib/util/constants";
import {
	ContainmentInfoCard,
	ContainmentTypeCard,
} from "pages/Upload/cards";
import * as cardLabels from "pages/Upload/cards/constants";
import { UploadContentTypeSelector, UploadContentType } from "./UploadContentTypeSelector";
import { ElevatedPaperStyle } from "lib/styles/shared";
import { LoadingButtonStyle } from "lib/styles/button";

export interface EditContainmentRef {
	resetForm: () => void;
	hasChanges: () => boolean;
}

export interface EditContainmentProps {
	c: Containment;
	onEdit: (updatedContainment: ContainmentDTO) => void;
	onClose?: () => void;
	blockNavigation?: boolean;
}

interface actionOption {
	value: string;
	label: string;
}

const flexnetActionOptions: actionOption[] = [
	{ value: "addSerials", label: cardLabels.AddSerials },
];

export const EditContainment = React.forwardRef<
	EditContainmentRef,
	EditContainmentProps
>(({ c, onEdit, onClose, blockNavigation }, ref) => {
	const { f, reset, isDirty } = hooks.UseEditingForm(c, {
		toDTO: (c) => c.ToDTO(),
	});
	const validator = util.GetFormValidator(f);
	const permissions = hooks.UseHasPermission();

	useImperativeHandle(ref, () => ({
		resetForm: reset,
		hasChanges: () => isDirty,
	}));

	const api = hooks.UseMccApi();
	const snack = hooks.UseSnackBar();
	const navigate = useNavigate();

	hooks.UseUnsavedChanges(
		() => (blockNavigation ? false : isDirty),
		() => {
			reset();
			onClose?.();
		}
	);

	const [loading, setLoading] = useState(false);
	const submit = useCallback(
		async (formData: ContainmentDTO) => {
			try {
				setLoading(true);
				const updated = await api.UpdateContainment(
					formData.id,
					formData
				);
				snack.open(result.Ok("Containment updated successfully"));
				onEdit(updated);
				reset(updated);
			} catch (err) {
				snack.open(
					result.Err(
						`Update failed: ${util.GetMsgFromError(err as Error)}`
					)
				);
			} finally {
				setLoading(false);
			}
		},
		[api, onEdit, reset, snack]
	);

	const typeConfig = c.TypeConfig;
	const actionOptions = useMemo(() => {
		if (typeConfig?.usesLockPermissions) {
			const options: actionOption[] = [];
			if (permissions.hasPermission(c, OBJECT_ID_ASRS_LOCK)) {
				options.push({ value: "lock", label: cardLabels.LockLpns });
			}
			if (permissions.hasPermission(c, OBJECT_ID_ASRS_UNLOCK)) {
				options.push({ value: "unlock", label: cardLabels.UnlockLpns });
			}
			return options;
		}
		if (permissions.hasPermission(c, OBJECT_ID_CREATE_CONTAINMENT)) {
			return flexnetActionOptions;
		}
		return [];
	}, [typeConfig, c, permissions]);

	const [selectedAction, setSelectedAction] = useState<actionOption | null>(null);
	const [selectedContentType, setSelectedContentType] = useState<UploadContentType | null>(null);

	useEffect(() => {
		if (actionOptions.length === 1) {
			setSelectedAction(actionOptions[0]);
		}
	}, [actionOptions]);

	const containmentTypeLabel = useMemo(() => {
		return c.SourceSystem || undefined;
	}, [c]);

	const canEdit = typeConfig?.usesLockPermissions
		? permissions.hasPermission(c, OBJECT_ID_ASRS_LOCK)
		: permissions.hasPermission(c, OBJECT_ID_CREATE_CONTAINMENT);

	const handleNext = useCallback(() => {
		if (!selectedAction) return;
		if (selectedAction.value === "addSerials") {
			navigate(routes.Upload.PathWithParams(c.id));
		} else {
			const actionParam = selectedAction.value === "lock" ? "lock" : "unlock";
			const byLpn = selectedContentType?.label === UploadContentTypeLPN;
			navigate(`${routes.Upload.PathWithParams(c.id)}?action=${actionParam}&byLpn=${byLpn}`);
		}
	}, [navigate, c.id, selectedAction, selectedContentType]);

	const theme = useTheme();

	const cancelButtonSx = {
		px: 4,
		py: 1.2,
		borderRadius: 20,
		textTransform: "none",
		fontSize: "0.95rem",
		fontWeight: 400,
		letterSpacing: "0.01em",
	} as const;

	return (
		<Box display="flex" flexDirection="column" gap={3}>
			<form onSubmit={f.handleSubmit(submit)}>
				<Paper elevation={0} sx={ElevatedPaperStyle(theme)}>
					<Box display="flex" flexDirection="column" gap={2.5}>
						<ContainmentInfoCard
							control={f.control}
							mccId={c.friendlyId}
							fieldNames={{ containmentName: "name" }}
							disabledFields={{
								containmentName: true,
								description: !canEdit,
							}}
						/>

						<ContainmentTypeCard
							control={f.control}
							containmentTypeLabel={containmentTypeLabel}
							containmentTypeOptions={
								containmentTypeLabel
									? [{ value: containmentTypeLabel.toLowerCase(), label: containmentTypeLabel }]
									: []
							}
							containmentType={containmentTypeLabel?.toLowerCase()}
							typeConfig={c.TypeConfig}
							initialHoldName={c.initial?.name}
							subsequentHoldName={c.subsequent?.name}
							fieldNames={{ initialHold: "initial", subsequentHold: "subsequent" }}
							getHoldOptionDisabled={(option) => {
								const optionName = option.name?.toLowerCase();
								if (optionName === "contain" || optionName === "contained") {
									return !canEdit;
								}
								if (optionName === "hold") {
									return !permissions.hasPermission(c, OBJECT_ID_HOLD_CONTAINMENT);
								}
								return false;
							}}
							onInitialDataLoad={(d) => {
								if (!d.IsEmpty() && !f.getValues("initial")) {
									f.setValue(
										"initial",
										d.First().Expect("first el should exist")
									);
								}
							}}
						disabledFields={{
							containmentType: true,
							initialHold: true,
						}}
					/>

						<Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", pt: 1 }}>
							{isDirty && (
								<Button
									variant="outlined"
									color="inherit"
									onClick={() => reset()}
									sx={cancelButtonSx}
								>
									Cancel
								</Button>
							)}
							<LoadingButton
								data-test-selector="next-button"
								disabled={
									!isDirty ||
									!validator(
										c.TypeConfig?.hasHolds
											? ["description", "subsequent", "initial"]
											: ["description"]
									)
								}
								type="submit"
								variant="contained"
								loading={loading}
							>
								Update Containment
							</LoadingButton>
						</Box>
					</Box>
				</Paper>
			</form>

			{actionOptions.length > 0 && (
			<>
			<Divider />

			<FormCard
				label={cardLabels.Actions}
				toolTip={typeConfig?.usesLockPermissions
					? "Lock or unlock LPNs on this containment"
					: "Add serials to this containment"
				}
			>
				<Box display="flex" flexDirection="column" gap={2}>
					<Box>
						<Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.5 }}>
							{cardLabels.Action}
						</Typography>
						<Dropdown
							data={actionOptions}
							value={selectedAction}
							getOptionLabel={(o) => o.label}
							isOptionEqualToValue={(a, b) => a.value === b.value}
						onChange={(val) => {
							setSelectedAction(val);
							setSelectedContentType(null);
						}}
							fullWidth
							multiple={false}
							label={cardLabels.Action}
							hideLabel
							textFieldProps={{ placeholder: "Select action" }}
							clearable={false}
						/>
					</Box>

				{selectedAction && typeConfig?.showUploadContentType && (
					<UploadContentTypeSelector
						value={selectedContentType}
						onChange={setSelectedContentType}
					/>
				)}

					<Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
						{selectedAction && actionOptions.length > 1 && (
							<Button
								variant="outlined"
								color="inherit"
							onClick={() => {
								setSelectedAction(null);
								setSelectedContentType(null);
							}}
								sx={cancelButtonSx}
							>
								Cancel
							</Button>
						)}
						<Button
							data-test-selector="add-serials-inline-button"
							disabled={!selectedAction || (typeConfig?.showUploadContentType && !selectedContentType)}
							onClick={handleNext}
							sx={LoadingButtonStyle(theme)}
						>
							Next
						</Button>
					</Box>
				</Box>
			</FormCard>
			</>
			)}
		</Box>
	);
});
